// components/ui/ConfirmDialog.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmDialog = ({ 
  isOpen = false,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "danger" // danger, warning, info
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm?.();
    setIsVisible(false);
  };

  const handleCancel = () => {
    onCancel?.();
    setIsVisible(false);
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          border: 'border-red-500',
          icon: 'text-red-400',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white',
          backdrop: 'bg-red-900/10'
        };
      case 'warning':
        return {
          border: 'border-yellow-500',
          icon: 'text-yellow-400',
          confirmButton: 'bg-yellow-600 hover:bg-yellow-700 text-white',
          backdrop: 'bg-yellow-900/10'
        };
      case 'info':
      default:
        return {
          border: 'border-blue-500',
          icon: 'text-blue-400',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white',
          backdrop: 'bg-blue-900/10'
        };
    }
  };

  if (!isVisible) return null;

  const styles = getVariantStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm ${styles.backdrop}`}
        onClick={handleCancel}
      />
      
      {/* Dialog */}
      <div className={`
        relative bg-gray-900 rounded-lg border ${styles.border}
        shadow-2xl shadow-black/50 max-w-md w-full mx-4 p-6
        transform transition-all duration-200 scale-100
      `}>
        {/* Header */}
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className={`w-6 h-6 ${styles.icon}`} />
          <h3 className="text-lg font-semibold text-white">
            {title}
          </h3>
          <button
            onClick={handleCancel}
            className="ml-auto text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message */}
        <p className="text-gray-300 mb-6 leading-relaxed">
          {message}
        </p>

        {/* Actions */}
        <div className="flex space-x-3 justify-end">
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-lg transition-colors ${styles.confirmButton}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;